import React from 'react';
import { PAGINATION_CONFIG, getAvailablePageSizes } from '../config/pagination';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    currentItemCount: number;
    itemsPerPage: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
    showPageSizeSelector?: boolean;
    context?: keyof typeof PAGINATION_CONFIG.DEFAULT_PAGE_SIZES;
}

export const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalPages,
    totalItems,
    currentItemCount,
    itemsPerPage,
    onPageChange,
    onPageSizeChange,
    showPageSizeSelector = false,
    context = 'SEARCH_RESULTS'
}) => {
    const buttonStyle = {
        padding: '0.5rem 1rem',
        margin: '0 0.25rem',
        border: '1px solid #e2e8f0',
        borderRadius: '4px',
        backgroundColor: 'white',
        cursor: 'pointer',
        fontSize: '0.875rem',
        color: '#4a5568',
        transition: 'all 0.2s',
    };

    const disabledButtonStyle = {
        ...buttonStyle,
        opacity: 0.5,
        cursor: 'not-allowed',
    };

    const activePageStyle = {
        ...buttonStyle,
        backgroundColor: '#007bff',
        color: 'white',
        borderColor: '#007bff',
    };

    const pageSizeSelectStyle = {
        padding: '0.5rem',
        border: '1px solid #e2e8f0',
        borderRadius: '4px',
        fontSize: '0.875rem',
        marginRight: '1rem',
    };

    // Get available page sizes for the context
    const availablePageSizes = getAvailablePageSizes(context);

    // Generate page numbers to display
    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = PAGINATION_CONFIG.UI.MAX_VISIBLE_PAGE_NUMBERS;
        
        if (totalPages <= maxVisiblePages) {
            // Show all pages if total is small
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Show smart pagination with ellipsis
            if (currentPage <= 4) {
                // Near start: show first 5 pages + ellipsis + last page
                for (let i = 1; i <= 5; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 3) {
                // Near end: show first page + ellipsis + last 5 pages
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 4; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                // Middle: show first page + ellipsis + current ± 2 + ellipsis + last page
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 2; i <= currentPage + 2; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            }
        }
        
        return pages;
    };

    const pageNumbers = getPageNumbers();

    return (
        <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            gap: '1rem',
            flexWrap: 'wrap'
        }}>
            {/* Left side - Page size selector and info */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {showPageSizeSelector && onPageSizeChange && PAGINATION_CONFIG.UI.SHOW_PAGE_SIZE_IN_PAGINATION && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '0.875rem', color: '#666' }}>Show:</span>
                        <select
                            value={itemsPerPage}
                            onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
                            style={pageSizeSelectStyle}
                        >
                            {availablePageSizes.map(size => (
                                <option key={size} value={size}>{size}</option>
                            ))}
                        </select>
                    </div>
                )}
                
                {/* Results info */}
                {PAGINATION_CONFIG.UI.SHOW_RESULTS_SUMMARY && (
                    <div style={{ fontSize: '0.875rem', color: '#666' }}>
                        Showing {currentItemCount} of {totalItems} results
                        {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
                    </div>
                )}
            </div>

            {/* Right side - Pagination controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {/* Previous button */}
                <button
                    style={currentPage === 1 ? disabledButtonStyle : buttonStyle}
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    title="Previous page"
                >
                    ← Previous
                </button>

                {/* Page numbers */}
                {pageNumbers.map((page, index) => (
                    <React.Fragment key={index}>
                        {page === '...' ? (
                            <span style={{ 
                                padding: '0.5rem', 
                                color: '#666',
                                fontSize: '0.875rem'
                            }}>
                                ...
                            </span>
                        ) : (
                            <button
                                style={page === currentPage ? activePageStyle : buttonStyle}
                                onClick={() => onPageChange(page as number)}
                                title={`Go to page ${page}`}
                            >
                                {page}
                            </button>
                        )}
                    </React.Fragment>
                ))}

                {/* Next button */}
                <button
                    style={currentPage >= totalPages ? disabledButtonStyle : buttonStyle}
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    title="Next page"
                >
                    Next →
                </button>
            </div>
        </div>
    );
}; 